// 플랫폼 감지 — iOS 인앱 브라우저 분기(FR-AR-05/FR-COM-10), 카메라 지원 여부.
// 순수 함수(ua 주입)로 테스트 가능.

export interface PlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isInApp: boolean;
  inAppName?: string; // 카카오/인스타 등
  cameraSupported: boolean;
}

// 대표 인앱 브라우저 식별자(UA 토큰 → 표시명)
const IN_APP_SIGNS: { re: RegExp; name: string }[] = [
  { re: /KAKAOTALK/i, name: "카카오톡" },
  { re: /Instagram/i, name: "인스타그램" },
  { re: /\bLine\//i, name: "라인" },
  { re: /FBAN|FBAV|FB_IAB/i, name: "페이스북" },
  { re: /NAVER\(inapp|NAVER /i, name: "네이버" },
  { re: /DaumApps/i, name: "다음" },
  { re: /; wv\)/i, name: "인앱 브라우저" }, // 안드로이드 WebView 일반
];

export function detectPlatform(
  ua: string,
  cameraSupported: boolean,
): PlatformInfo {
  const isIOS =
    /iP(hone|od|ad)/.test(ua) ||
    // iPadOS 13+ 는 Mac 으로 위장 — 터치 가능 Mac 보정은 호출부에서.
    false;
  const isAndroid = /Android/i.test(ua);
  const sign = IN_APP_SIGNS.find((s) => s.re.test(ua));
  return {
    isIOS,
    isAndroid,
    isInApp: !!sign,
    inAppName: sign?.name,
    cameraSupported,
  };
}

/** 인앱 안내가 쓰는 기기 구분 — 메뉴 자리·이름이 아이폰과 안드로이드에서 다르다(shared `inAppMenuSay`). */
export function platformOs(p: Pick<PlatformInfo, "isIOS" | "isAndroid">): "ios" | "android" | "other" {
  return p.isIOS ? "ios" : p.isAndroid ? "android" : "other";
}

/** 현재 브라우저 환경에서 플랫폼 정보 산출. */
export function currentPlatform(): PlatformInfo {
  if (typeof navigator === "undefined") {
    return { isIOS: false, isAndroid: false, isInApp: false, cameraSupported: false };
  }
  const ua = navigator.userAgent;
  const cameraSupported =
    typeof navigator.mediaDevices?.getUserMedia === "function" &&
    typeof window !== "undefined" &&
    // 보안 컨텍스트(https/localhost)에서만 카메라 허용
    window.isSecureContext !== false;
  const info = detectPlatform(ua, cameraSupported);
  // iPadOS(Mac 위장) 보정: 터치 지원 Mac
  if (
    !info.isIOS &&
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  ) {
    info.isIOS = true;
  }
  return info;
}
